import { useState, useEffect } from "react";
import { useTransactionStatus } from "@/hooks/use-transaction-status";
import { TransactionStatusIndicator } from "./TransactionStatusIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExternalLink, RefreshCw, Receipt } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface TransactionHistoryPanelProps {
  coinId: string;
  className?: string;
}

export function TransactionHistoryPanel({ coinId, className }: TransactionHistoryPanelProps) {
  const { transactions, isLoading, error, getTransactionsForCoin } = useTransactionStatus(undefined, undefined);
  const [activeTab, setActiveTab] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const coinTransactions = getTransactionsForCoin(coinId);
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  const filteredTransactions = activeTab === "all"
    ? coinTransactions
    : coinTransactions.filter(tx => tx.status === activeTab);

  const visibleTransactions = showAllTransactions
    ? filteredTransactions
    : filteredTransactions.slice(0, 5);

  const refreshTransactions = async () => {
    setIsRefreshing(true);
    try {
      // Force refresh by reloading transactions
      const { data } = await supabase
        .from("trades")
        .select("*")
        .eq("coin_id", coinId)
        .order("created_at", { ascending: false })
        .limit(50);

      // This will trigger the real-time subscription to update
    } catch (error) {
      console.error("Error refreshing transactions:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-center py-4">
            <p>Failed to load transactions: {error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={refreshTransactions} disabled={isRefreshing}>
              {isRefreshing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Transaction History</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refreshTransactions} disabled={isRefreshing}>
            {isRefreshing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {coinTransactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Receipt className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Transactions Yet</h3>
            <p className="text-muted-foreground max-w-sm">
              Start trading this coin to see your transaction history.
            </p>
          </div>
        ) : (
          <>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
                <TabsTrigger value="failed">Failed</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="space-y-3">
              {visibleTransactions.map((transaction) => (
                <div key={transaction.id} className="p-3 rounded-lg border border-border">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-sm">
                        {transaction.tradeType === "buy" ? "Buy" : "Sell"} {transaction.amount.toFixed(6)} SOL
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {transaction.createdAt.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">
                        {transaction.price.toFixed(8)} SOL
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {transaction.amount.toFixed(6)} SOL
                      </p>
                    </div>
                  </div>

                  <TransactionStatusIndicator
                    status={transaction.status}
                    explorerUrl={transaction.txSignature ? `https://explorer.solana.com/tx/${transaction.txSignature}` : undefined}
                    error={transaction.error}
                    className="mt-2"
                  />
                </div>
              ))}

              {filteredTransactions.length > 5 && !showAllTransactions && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowAllTransactions(true)}
                >
                  Show All Transactions ({filteredTransactions.length})
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}